import { useRef, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { User, BadgeCheck, Copy, Check } from 'lucide-react';
import ChartRenderer from './ChartRenderer';
import TableRenderer from './TableRenderer';
import FailureReportRenderer from './FailureReportRenderer';
import CollapsibleSQL from './CollapsibleSQL';
import { FeedbackButtons } from './FeedbackButtons';
import { CodeBlock } from './CodeBlock';
import './MessageList.css';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: Date;
  chartData?: any; // For chart data
  tableData?: any; // For table data
  failureReportData?: any; // For failure report data
  eventId?: number; // For feedback tracking
}

interface MessageListProps {
  messages: Message[];
  streamedContent: string;
  isStreaming: boolean;
  onFeedback: (eventId: number, type: 'positive' | 'negative', comment?: string) => Promise<void>;
}

function MessageList({ messages, streamedContent, isStreaming, onFeedback }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  // Copy message content to clipboard
  const handleCopyMessage = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  // Scroll to bottom when messages or streaming content updates
  useEffect(() => {
    // Only scroll if there are messages or streaming content
    if (messages.length > 0 || streamedContent) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamedContent]);

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Convert plain URLs to markdown links (for URLs not already in markdown link syntax)
  const autoLinkUrls = (text: string): string => {
    // Regex to find URLs that are NOT already inside markdown link syntax
    // Negative lookbehind (?<!\]\() ensures we don't match URLs already in [text](url)
    // Negative lookbehind (?<!\() ensures we don't match URLs already in (url)
    const urlRegex = /(?<!\]\()(?<!\()(https?:\/\/[^\s)>\]]+)/g;

    return text.replace(urlRegex, (url) => {
      // Clean up trailing punctuation that might have been captured
      let cleanUrl = url;
      const trailingPunctuation = /[.,;:!?)]+$/;
      const match = cleanUrl.match(trailingPunctuation);
      let trailing = '';
      if (match) {
        // Keep ) if it looks like part of URL path, remove others
        if (match[0] !== ')' || !cleanUrl.includes('(')) {
          trailing = match[0];
          cleanUrl = cleanUrl.slice(0, -match[0].length);
        }
      }

      // Create a friendly link text with appropriate emoji
      let linkText = '🔗 View Link';
      if (cleanUrl.includes('/visualizations/') || cleanUrl.includes('/lineage')) {
        linkText = '🔀 View Data Lineage';
      } else if (cleanUrl.includes('/static/')) {
        linkText = '📄 View Resource';
      }

      return `[${linkText}](${cleanUrl})${trailing}`;
    });
  };

  // Parse content to extract SQL blocks, table data, and chart data
  const parseContentWithSQL = (content: string) => {
    // Regex to match SQL blocks with 3 capture groups:
    // Group 1: description after SQL_START
    // Group 2: SQL content between SQL_START and SQL_END
    // Group 3: any junk text between SQL_END and closing ``` (LLM violations)
    const sqlBlockRegex = /```sql\s*\n--\s*SQL_START:\s*([^\n]+)\n([\s\S]*?)--\s*SQL_END([\s\S]*?)```/gi;

    // Regex to match table data blocks: TABLE_DATA_START\n{...}\nTABLE_DATA_END
    const tableDataRegex = /TABLE_DATA_START\s*\n(\{[\s\S]*?\})\s*\nTABLE_DATA_END/gi;

    // Regex to match chart data blocks - SAME pattern as table data
    // Uses \s* to handle any whitespace (including newlines) around the JSON
    const chartDataRegex = /CHART_DATA_START\s*(\{[\s\S]*?\})\s*CHART_DATA_END/gi;

    // Regex to match failure report blocks - support-friendly failure report
    const failureReportRegex = /FAILURE_REPORT_START\s*(\{[\s\S]*?\})\s*FAILURE_REPORT_END/gi;

    // Helper: Sanitize Python-style JSON (True/False/None -> true/false/null)
    const sanitizePythonJson = (str: string): string => {
      return str
        .replace(/:\s*True\b/g, ': true')
        .replace(/:\s*False\b/g, ': false')
        .replace(/:\s*None\b/g, ': null')
        .replace(/,\s*True\b/g, ', true')
        .replace(/,\s*False\b/g, ', false')
        .replace(/,\s*None\b/g, ', null')
        .replace(/\[\s*True\b/g, '[true')
        .replace(/\[\s*False\b/g, '[false')
        .replace(/\[\s*None\b/g, '[null');
    };

    const parts: Array<{type: 'text' | 'sql' | 'table' | 'chart' | 'failure_report', content: string, description?: string, tableData?: any, chartData?: any, failureReportData?: any}> = [];

    // Step 1: Find all special blocks (tables, charts, failure reports) and their positions
    interface BlockMatch {
      type: 'table' | 'chart' | 'failure_report';
      start: number;
      end: number;
      data: any;
    }
    const blocks: BlockMatch[] = [];

    // Find table blocks
    let match;
    tableDataRegex.lastIndex = 0;
    while ((match = tableDataRegex.exec(content)) !== null) {
      try {
        const tableData = JSON.parse(match[1]);
        blocks.push({
          type: 'table',
          start: match.index,
          end: match.index + match[0].length,
          data: tableData
        });
      } catch (e) {
        // Table JSON parse failed
      }
    }

    // Find chart blocks (sanitizePythonJson is defined earlier)
    chartDataRegex.lastIndex = 0;
    while ((match = chartDataRegex.exec(content)) !== null) {
      try {
        const rawJson = match[1];
        const sanitizedJson = sanitizePythonJson(rawJson);
        const chartData = JSON.parse(sanitizedJson);
        blocks.push({
          type: 'chart',
          start: match.index,
          end: match.index + match[0].length,
          data: chartData
        });
      } catch (e) {
        // Chart JSON parse failed
      }
    }

    // Find failure report blocks
    failureReportRegex.lastIndex = 0;
    while ((match = failureReportRegex.exec(content)) !== null) {
      try {
        const rawJson = match[1];
        const sanitizedJson = sanitizePythonJson(rawJson);
        const reportData = JSON.parse(sanitizedJson);
        blocks.push({
          type: 'failure_report',
          start: match.index,
          end: match.index + match[0].length,
          data: reportData
        });
      } catch (e) {
        // Failure report JSON parse failed
      }
    }

    // FALLBACK: Detect raw chart JSON without markers (LLM sometimes strips the markers)
    // Look for JSON objects that look like chart data: {"type": "bar|timeline|execution_history|line", "data": ...}
    if (blocks.filter(b => b.type === 'chart').length === 0) {
      // Regex to find raw chart JSON - matches {"type": "chartType", followed by more JSON
      const rawChartJsonRegex = /\{[\s\n]*"type"[\s\n]*:[\s\n]*"(bar|timeline|execution_history|line|image|interactive_html)"[\s\n]*,[\s\S]*?"data"[\s\n]*:[\s\S]*?\}(?=\s*(?:\n\n|$|The |the |This |this |I |Here|Based|Note|⚠️|📊|✅|❌|\*\*|•|-))/gi;

      rawChartJsonRegex.lastIndex = 0;
      while ((match = rawChartJsonRegex.exec(content)) !== null) {
        try {
          const rawJson = match[0];
          // Try to find the complete JSON object by matching braces
          let braceCount = 0;
          let endIndex = 0;
          for (let i = 0; i < rawJson.length; i++) {
            if (rawJson[i] === '{') braceCount++;
            if (rawJson[i] === '}') braceCount--;
            if (braceCount === 0) {
              endIndex = i + 1;
              break;
            }
          }

          const jsonStr = rawJson.substring(0, endIndex);
          const sanitizedJson = sanitizePythonJson(jsonStr);
          const chartData = JSON.parse(sanitizedJson);

          // Validate it looks like chart data
          if (chartData.type && (chartData.data || chartData.daily_execution_counts)) {
            blocks.push({
              type: 'chart',
              start: match.index,
              end: match.index + jsonStr.length,
              data: chartData
            });
          }
        } catch (e) {
          // Raw chart JSON parse failed
        }
      }
    }

    // Sort blocks by position
    blocks.sort((a, b) => a.start - b.start);

    // Step 2: Process content with blocks
    let lastIndex = 0;

    // Process each block (table or chart)
    for (const block of blocks) {
      // Add text before this block
      if (block.start > lastIndex) {
        const beforeText = content.substring(lastIndex, block.start);
        // Check for SQL blocks in the text before this block
        const sqlMatches = Array.from(beforeText.matchAll(sqlBlockRegex));
        let textLastIndex = 0;

        sqlMatches.forEach(sqlMatch => {
          if (sqlMatch.index !== undefined && sqlMatch.index > textLastIndex) {
            parts.push({
              type: 'text',
              content: beforeText.substring(textLastIndex, sqlMatch.index)
            });
          }
          parts.push({
            type: 'sql',
            content: sqlMatch[2].trim(),
            description: sqlMatch[1].trim()
          });
          // Handle junk text between SQL_END and ``` (LLM violations)
          if (sqlMatch[3] && sqlMatch[3].trim()) {
            parts.push({
              type: 'text',
              content: sqlMatch[3].trim()
            });
          }
          textLastIndex = sqlMatch.index! + sqlMatch[0].length;
        });

        if (textLastIndex < beforeText.length) {
          parts.push({
            type: 'text',
            content: beforeText.substring(textLastIndex)
          });
        }
      }

      // Add the block (table or chart)
      if (block.type === 'table') {
        parts.push({
          type: 'table',
          content: '',
          tableData: block.data
        });
      } else if (block.type === 'chart') {
        parts.push({
          type: 'chart',
          content: '',
          chartData: block.data
        });
      } else if (block.type === 'failure_report') {
        parts.push({
          type: 'failure_report',
          content: '',
          failureReportData: block.data
        });
      }

      lastIndex = block.end;
    }

    // Process remaining content for SQL blocks
    const remainingContent = content.substring(lastIndex);
    if (remainingContent) {
      const sqlMatches = Array.from(remainingContent.matchAll(sqlBlockRegex));
      let textLastIndex = 0;

      sqlMatches.forEach(sqlMatch => {
        if (sqlMatch.index !== undefined && sqlMatch.index > textLastIndex) {
          parts.push({
            type: 'text',
            content: remainingContent.substring(textLastIndex, sqlMatch.index)
          });
        }
        parts.push({
          type: 'sql',
          content: sqlMatch[2].trim(),
          description: sqlMatch[1].trim()
        });
        // Handle junk text between SQL_END and ``` (LLM violations)
        if (sqlMatch[3] && sqlMatch[3].trim()) {
          parts.push({
            type: 'text',
            content: sqlMatch[3].trim()
          });
        }
        textLastIndex = sqlMatch.index! + sqlMatch[0].length;
      });

      if (textLastIndex < remainingContent.length) {
        parts.push({
          type: 'text',
          content: remainingContent.substring(textLastIndex)
        });
      }
    }

    // If no blocks found, return original content as text
    if (parts.length === 0) {
      parts.push({
        type: 'text',
        content: content
      });
    }

    // Final cleanup: Remove any stray markers from text parts and filter empty parts
    // This follows the same simple pattern as table rendering
    const cleanedParts = parts.map(part => {
      if (part.type === 'text') {
        let cleaned = part.content
          // FIX: Handle malformed single-backtick code blocks
          // The LLM sometimes outputs `sql instead of ```sql
          // Just remove these malformed markers - the SQL will show as plain text which is acceptable
          .replace(/[`'](sql|python|javascript|typescript|bash|json|yaml|markdown|text|plaintext)[`']?\s*\n/gi, '\n')
          // Remove chart markers
          .replace(/CHART_DATA_START\s*/gi, '')
          .replace(/\s*CHART_DATA_END/gi, '')
          // Remove table markers
          .replace(/TABLE_DATA_START\s*/gi, '')
          .replace(/\s*TABLE_DATA_END/gi, '')
          // Remove failure report markers
          .replace(/FAILURE_REPORT_START\s*/gi, '')
          .replace(/\s*FAILURE_REPORT_END/gi, '')
          // Remove empty code fences (triple backticks with nothing or whitespace between)
          .replace(/```[\s\n]*```/g, '')
          // Remove empty inline code (single backticks with nothing or whitespace between)
          .replace(/`[\s]*`/g, '')
          // Remove standalone triple backticks on their own line
          .replace(/^\s*```\s*$/gm, '')
          // Remove standalone single backticks on their own line
          .replace(/^\s*`\s*$/gm, '')
          // Remove lines that are just backticks with optional language hint (case-insensitive for Markdown/JSON/etc.)
          .replace(/^\s*```[a-zA-Z]*\s*$/gm, '')
          // Remove standalone language hint words at start of content (remnants of stripped code fences)
          .replace(/^\s*(markdown|Markdown|plaintext|text)\s*\n/gm, '')
          // Clean up multiple consecutive newlines (more than 2)
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        // Auto-link plain URLs (convert http://... to clickable markdown links)
        cleaned = autoLinkUrls(cleaned);
        return { ...part, content: cleaned };
      }
      return part;
    }).filter(part => {
      // Remove empty text parts
      if (part.type === 'text' && !part.content.trim()) {
        return false;
      }
      return true;
    });

    return cleanedParts.length > 0 ? cleanedParts : parts;
  };

  return (
    <div className="message-list">
      {messages.map(message => (
        // System messages render differently - centered, no avatar
        message.role === 'system' ? (
          <div key={message.id} className="message-wrapper system-wrapper">
            <div className="system-message">
              <span className="system-message-content">{message.content}</span>
              <span className="system-message-time">{formatTimestamp(message.timestamp)}</span>
            </div>
          </div>
        ) : (
        <div key={message.id} className={`message-wrapper ${message.role}-wrapper`}>
          <div className="message-avatar">
            {message.role === 'user' ? (
              <User size={20} />
            ) : (
              <img src={`${import.meta.env.BASE_URL}favicon.ico`} alt="Nomi" className="avatar-icon" />
            )}
          </div>
          <div className={`message-bubble ${message.role}-message`}>
            <div className="message-header">
              <div className="message-sender-info">
                <span className="message-sender">
                  {message.role === 'user' ? 'You' : 'NOMI'}
                </span>
                {message.role === 'assistant' && (
                  <BadgeCheck size={14} className="verified-badge" />
                )}
              </div>
              <span className="message-timestamp">
                {formatTimestamp(message.timestamp)}
              </span>
            </div>
            <div className="message-content">
              {parseContentWithSQL(message.content).map((part, index) => {
                if (part.type === 'sql') {
                  return (
                    <CollapsibleSQL
                      key={`sql-${index}`}
                      sql={part.content}
                      description={part.description}
                    />
                  );
                } else if (part.type === 'table') {
                  return (
                    <TableRenderer
                      key={`table-${index}`}
                      tableData={part.tableData}
                    />
                  );
                } else if (part.type === 'chart') {
                  return (
                    <ChartRenderer
                      key={`chart-${index}`}
                      chartData={part.chartData}
                    />
                  );
                } else if (part.type === 'failure_report') {
                  return (
                    <FailureReportRenderer
                      key={`failure-report-${index}`}
                      reportData={part.failureReportData}
                    />
                  );
                } else {
                  return (
                    <ReactMarkdown
                      key={`text-${index}`}
                      rehypePlugins={[]}
                      components={{
                      // Custom components for better styling
                      p: ({ children }) => (
                        <p style={{ whiteSpace: 'pre-wrap' }}>{children}</p>
                      ),
                      code: ({ children, className, node }) => {
                        const content = String(children).replace(/\n$/, '');

                        // Detect if this is a code block vs inline code
                        // Check if parent is <pre> (hast node structure)
                        const isBlock = node?.position &&
                          node.position.start.line !== node.position.end.line;
                        const hasNewlines = content.includes('\n');
                        const isInline = !isBlock && !hasNewlines && !className;

                        // Extract language from className (e.g., "language-sql" -> "sql")
                        const language = className?.replace('language-', '');

                        return (
                          <CodeBlock inline={isInline} language={language}>
                            {content}
                          </CodeBlock>
                        );
                      },
                      ul: ({ children }) => (
                        <ul className="markdown-list">{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="markdown-list">{children}</ol>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="markdown-quote">{children}</blockquote>
                      ),
                      h1: ({ children }) => <h1 className="markdown-h1">{children}</h1>,
                      h2: ({ children }) => <h2 className="markdown-h2">{children}</h2>,
                      h3: ({ children }) => <h3 className="markdown-h3">{children}</h3>,
                      // Custom em component - add typing-indicator class ONLY for typing messages
                      em: ({ children }) => {
                        const text = String(children);
                        // Check if this is a typing indicator (status messages like "Loading...", "Fetching...", etc.)
                        const isTypingIndicator = text.match(/^(Fetching|Loading|Retrieving|Working|Running|Analyzing|Building|Transforming|Accessing|Checking|Creating|Executing|Generating|Processing|Updating)/);
                        return <em className={isTypingIndicator ? "typing-indicator" : ""}>{children}</em>;
                      },
                      // Custom link component to handle external links
                      a: ({ children, href, title }) => {
                        const isExternalLink = href && (href.startsWith('http') || href.startsWith('https'));
                        return (
                          <a
                            href={href}
                            target={isExternalLink ? "_blank" : undefined}
                            rel={isExternalLink ? "noopener noreferrer" : undefined}
                            title={title}
                            style={{ color: '#4169E1', textDecoration: 'underline' }}
                          >
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {part.content}
                  </ReactMarkdown>
                  );
                }
              })}

              {/* Render chart if chart data is present */}
              {message.chartData && (
                <div>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    📊 Rendering chart: {message.chartData.title}
                  </p>
                  <ChartRenderer chartData={message.chartData} />
                </div>
              )}

              {/* Render table if table data is present */}
              {message.tableData && (
                <div>
                  <TableRenderer tableData={message.tableData} />
                </div>
              )}

              {/* Render failure report if failure report data is present */}
              {message.failureReportData && (
                <div>
                  <FailureReportRenderer reportData={message.failureReportData} />
                </div>
              )}

              {/* Action buttons for assistant messages */}
              {message.role === 'assistant' && (
                <div className="message-actions">
                  <button
                    className={`action-btn copy-btn ${copiedMessageId === message.id ? 'copied' : ''}`}
                    onClick={() => handleCopyMessage(message.id, message.content)}
                    title="Copy message"
                  >
                    {copiedMessageId === message.id ? (
                      <>
                        <Check size={14} />
                        <span>Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        <span>Copy</span>
                      </>
                    )}
                  </button>

                  {/* Render feedback buttons for assistant messages with event_id */}
                  {message.eventId && (
                    <FeedbackButtons
                      eventId={message.eventId}
                      onFeedback={onFeedback}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        )
      ))}

      {isStreaming && streamedContent && (
        <div className="message-wrapper assistant-wrapper">
          <div className="message-avatar">
            <img src={`${import.meta.env.BASE_URL}favicon.ico`} alt="Nomi" className="avatar-icon" />
          </div>
          <div className="message-bubble assistant-message streaming">
            <div className="message-header">
              <div className="message-sender-info">
                <span className="message-sender">NOMI</span>
                <BadgeCheck size={14} className="verified-badge" />
              </div>
              <span className="message-timestamp">
                {formatTimestamp(new Date())}
              </span>
            </div>
            <div className="message-content">
              {parseContentWithSQL(streamedContent).map((part, index) => {
                if (part.type === 'sql') {
                  return (
                    <CollapsibleSQL
                      key={`sql-stream-${index}`}
                      sql={part.content}
                      description={part.description}
                    />
                  );
                } else if (part.type === 'table') {
                  return (
                    <TableRenderer
                      key={`table-stream-${index}`}
                      tableData={part.tableData}
                    />
                  );
                } else if (part.type === 'chart') {
                  return (
                    <ChartRenderer
                      key={`chart-stream-${index}`}
                      chartData={part.chartData}
                    />
                  );
                } else if (part.type === 'failure_report') {
                  return (
                    <FailureReportRenderer
                      key={`failure-report-stream-${index}`}
                      reportData={part.failureReportData}
                    />
                  );
                } else {
                  return (
                    <ReactMarkdown
                      key={`text-stream-${index}`}
                      components={{
                      p: ({ children }) => (
                        <p style={{ whiteSpace: 'pre-wrap' }}>{children}</p>
                      ),
                      code: ({ children, className, node }) => {
                        const content = String(children).replace(/\n$/, '');

                        // Detect if this is a code block vs inline code
                        // Check if parent is <pre> (hast node structure)
                        const isBlock = node?.position &&
                          node.position.start.line !== node.position.end.line;
                        const hasNewlines = content.includes('\n');
                        const isInline = !isBlock && !hasNewlines && !className;

                        // Extract language from className (e.g., "language-sql" -> "sql")
                        const language = className?.replace('language-', '');

                        return (
                          <CodeBlock inline={isInline} language={language}>
                            {content}
                          </CodeBlock>
                        );
                      },
                      ul: ({ children }) => (
                        <ul className="markdown-list">{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="markdown-list">{children}</ol>
                      ),
                      // Custom em component - add typing-indicator class ONLY for typing messages
                      em: ({ children }) => {
                        const text = String(children);
                        // Check if this is a typing indicator (status messages like "Loading...", "Fetching...", etc.)
                        const isTypingIndicator = text.match(/^(Fetching|Loading|Retrieving|Working|Running|Analyzing|Building|Transforming|Accessing|Checking|Creating|Executing|Generating|Processing|Updating)/);
                        return <em className={isTypingIndicator ? "typing-indicator" : ""}>{children}</em>;
                      },
                      // Custom link component to handle external links
                      a: ({ children, href, title }) => {
                        const isExternalLink = href && (href.startsWith('http') || href.startsWith('https'));
                        return (
                          <a
                            href={href}
                            target={isExternalLink ? "_blank" : undefined}
                            rel={isExternalLink ? "noopener noreferrer" : undefined}
                            title={title}
                            style={{ color: '#4169E1', textDecoration: 'underline' }}
                          >
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {part.content}
                  </ReactMarkdown>
                  );
                }
              })}
              <span className="typing-cursor">|</span>
            </div>
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}

export default MessageList;
